import React, {useRef, useState} from 'react';
import {Upload, X, File, Image} from 'lucide-react';
import {AppText, AppSmallText, AppBodyText, AppLabelText} from '../../../../constants';
import {useNotification} from '../../../../Services/useNotification';

interface FileUploadFieldProps {
  label: string;
  value?: File | File[] | null;
  onChange: (file: File | File[] | null) => void;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSizeInMB?: number;
  className?: string;
  error?: string;
  existingImages?: string[]; // URLs of existing images
  onRemoveExistingImage?: (imageUrl: string) => void; // Callback to remove existing image
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  label,
  value,
  onChange,
  accept = "image/*",
  multiple = false,
  maxFiles = 5,
  maxSizeInMB = 5,
  className = "",
  error,
  existingImages = [],
  onRemoveExistingImage
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const {error: showError} = useNotification();

  const handleFileSelect = (files: FileList | null) => {
    if (!files) {return;}

    const fileArray = Array.from(files);

    // Validate file size
    const oversizedFiles = fileArray.filter(file => file.size > maxSizeInMB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      showError(`بعض الملفات أكبر من ${maxSizeInMB}MB`);
      return;
    }

    // Validate number of files
    if (multiple) {
      if (fileArray.length > maxFiles) {
        showError(`يمكن رفع ${maxFiles} ملفات كحد أقصى`);
        return;
      }
      onChange(fileArray);
    } else {
      onChange(fileArray[0] || null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeFile = (index?: number) => {
    if (multiple && Array.isArray(value) && index !== undefined) {
      const newFiles = value.filter((_, i) => i !== index);
      onChange(newFiles.length > 0 ? newFiles : null);
    } else {
      onChange(null);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) {return '0 Bytes';}
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))  } ${  sizes[i]}`;
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="w-4 h-4 text-brand-500" />;
    }
    return <File className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block font-medium text-gray-700">
        {label}
      </label>

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-brand-500 bg-brand-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${error ? 'border-red-500' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={openFileDialog}
      >
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <AppText className="text-gray-600 mb-1">
          {multiple ? 'اسحب الملفات هنا أو انقر للاختيار' : 'اسحب الملف هنا أو انقر للاختيار'}
        </AppText>
        <AppSmallText className="text-gray-500">
          {accept === "image/*" ? 'الصور المدعومة: JPG, PNG, GIF' : 'جميع أنواع الملفات'}
        </AppSmallText>
        <AppSmallText className="text-gray-500">
          الحد الأقصى: {maxSizeInMB}MB {multiple && `- ${maxFiles} ملفات`}
        </AppSmallText>
      </div>

      {/* Error Message */}
      {error && (
        <AppBodyText className="text-red-600">{error}</AppBodyText>
      )}

      {/* Existing Images */}
      {existingImages && existingImages.length > 0 && (
        <div className="space-y-2">
          <AppLabelText className="font-medium text-gray-700">الصور الحالية:</AppLabelText>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {existingImages.map((imageUrl, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={imageUrl}
                    alt={`صورة موجودة ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                {onRemoveExistingImage && (
                  <button
                    type="button"
                    onClick={() => onRemoveExistingImage(imageUrl)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    title="حذف الصورة"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Files */}
      {value && (
        <div className="space-y-2">
          {Array.isArray(value) ? (
            value.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  {getFileIcon(file)}
                  <div>
                    <AppBodyText className="font-medium text-gray-900">{file.name}</AppBodyText>
                    <AppSmallText className="text-gray-500">{formatFileSize(file.size)}</AppSmallText>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          ) : (
            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-3">
                {getFileIcon(value)}
                <div>
                  <AppBodyText className="font-medium text-gray-900">{value.name}</AppBodyText>
                  <AppSmallText className="text-gray-500">{formatFileSize(value.size)}</AppSmallText>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile()}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
